import { useState, useRef, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { auth } from '../firebaseClient';

const createProgressClientId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const useAnalysisProcess = (analysisParams, setErrorExt) => {
  const {
    foundersFile,
    startDate,
    endDate,
    postThreshold,
    analysisMode,
    // analysisError, // Ошибку анализа будем устанавливать через setErrorExt
    // setAnalysisError // Этот сеттер внешний
  } = analysisParams;

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [processingStage, setProcessingStage] = useState(null);
  const [stageDetails, setStageDetails] = useState({ processed: 0, total: 0 });
  const [estimatedTimeLeft, setEstimatedTimeLeft] = useState('');
  const [elapsedTime, setElapsedTime] = useState('');
  const [processingSpeed, setProcessingSpeed] = useState('');
  const [currentItem, setCurrentItem] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [analysisEndTime, setAnalysisEndTime] = useState(null);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [vkAnalysisResult, setVkAnalysisResult] = useState(null);

  const eventSource = useRef(null); // Референс для SSE остается здесь
  const requestAbortController = useRef(null);
  const currentTaskIdRef = useRef(null);
  const activeProgressClientIdRef = useRef(null);
  const cancelRequestedRef = useRef(false);

  const resetAnalysisState = useCallback(() => {
    setIsLoading(false);
    setProgress(0);
    setStatusMessage('');
    setProcessingStage(null);
    setStageDetails({ processed: 0, total: 0 });
    setVkAnalysisResult(null);
    // setErrorExt(null); // Внешняя ошибка сбрасывается там, где используется хук
    setStartTime(null);
    setAnalysisEndTime(null);
    setCurrentItem('');
    setEstimatedTimeLeft('');
    setProcessingSpeed('');
    // currentTaskId сбрасывается отдельно при старте нового анализа или при отмене
  }, [/* setErrorExt */]); // setErrorExt если будет использоваться напрямую
  
  const handleSSEProgress = useCallback((event) => {
    console.log('[SSE Received Raw]:', event.data);
    try {
      const data = JSON.parse(event.data);
      if (data.message === 'Connected') return;
      if (data.progressClientId && activeProgressClientIdRef.current && data.progressClientId !== activeProgressClientIdRef.current) {
        return;
      }

      if (data.taskId) {
        currentTaskIdRef.current = data.taskId;
        setCurrentTaskId(data.taskId);
        console.log('Received task ID from server:', data.taskId);
      }

      const { stage, processed, total, estimatedTimeLeft: etl, speed, currentGroup } = data;
      setProcessingStage(stage);
      setStageDetails({ processed: processed || 0, total: total || 0 });
      setEstimatedTimeLeft(etl !== undefined && etl !== null ? etl : 'вычисление...');
      setProcessingSpeed(speed !== undefined && speed !== null ? speed : 'измерение...');
      setCurrentItem(currentGroup !== undefined && currentGroup !== null ? currentGroup : '');

      let progressValue = 0;
      let newMessage = '';

      if (stage === 'task_created') {
        newMessage = data.message || 'Задача создана на сервере...';
      } else if (stage === 'resolving_groups') {
        progressValue = Math.round(((processed || 0) / (total || 1)) * 30);
        newMessage = `Получение информации о группах (${processed || 0}/${total || 0})`;
      } else if (stage === 'fetching_posts') {
        progressValue = 30 + Math.round(((processed || 0) / (total || 1)) * 60);
        newMessage = `Получение постов из групп (${processed || 0}/${total || 0})`;
      } else if (stage === 'processing_data') {
        progressValue = 90 + Math.round(((processed || 0) / (total || 1)) * 10);
        newMessage = `Анализ данных (${processed || 0}/${total || 0})`;
      } else if (stage === 'cancelled') {
        newMessage = data.message || 'Анализ отменен на сервере.';
        setIsLoading(false); // Останавливаем загрузку
        setProgress(0); // Сбрасываем прогресс
        setVkAnalysisResult(null);
        // resetAnalysisState(); // Можно вызвать, но аккуратно с currentTaskId
      } else if (stage === 'error') {
        newMessage = `Ошибка на сервере: ${data.error || 'Неизвестная ошибка'}`;
        setErrorExt(data.error || 'Неизвестная ошибка сервера во время анализа.');
        setIsLoading(false);
        setProgress(0);
      }
      setProgress(progressValue);
      setStatusMessage(newMessage);

    } catch (error) {
      console.error('Error parsing SSE event:', error);
      setErrorExt('Ошибка обработки данных от сервера (SSE).');
      setIsLoading(false);
    }
  }, [setErrorExt]);

  const apiBaseUrlFromEnv = (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/$/, '');
  const fallbackBaseUrl = typeof window !== 'undefined'
    ? (window.location.hostname === 'localhost' ? 'http://localhost:3001' : window.location.origin)
    : 'http://localhost:3001';
  const resolvedApiBaseUrlRef = useRef(apiBaseUrlFromEnv || fallbackBaseUrl);

  const fetchDataFromVK = useCallback(async () => {
    if (!foundersFile) {
      setStatusMessage('Файл с учредителями не загружен.');
      setErrorExt('Файл с учредителями не загружен.');
      return;
    }
    if (!startDate || !endDate) {
      setStatusMessage('Пожалуйста, выберите даты начала и окончания периода.');
      setErrorExt('Пожалуйста, выберите даты начала и окончания периода.');
      return;
    }
    try {
      const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
      const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
      const startDateObj = new Date(Date.UTC(startYear, startMonth - 1, startDay, 3, 0, 0));
      const endDateObj = new Date(Date.UTC(endYear, endMonth - 1, endDay, 3, 0, 0));
      if (startDateObj > endDateObj) {
        setStatusMessage('Ошибка: начальная дата не может быть больше конечной.');
        setErrorExt('Ошибка: начальная дата не может быть больше конечной.');
        return;
      }
    } catch (error) {
      console.error('Ошибка при проверке дат:', error);
      setStatusMessage('Ошибка: некорректный формат дат.');
      setErrorExt('Ошибка: некорректный формат дат.');
      return;
    }

    if (analysisMode === 'monthly_4weeks') {
      const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
      const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
      const currentMoscowDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Moscow' }));
      const selectedMonth = `${startYear}-${String(startMonth).padStart(2, '0')}`;
      const currentMonth = `${currentMoscowDate.getFullYear()}-${String(currentMoscowDate.getMonth() + 1).padStart(2, '0')}`;

      if (startYear !== endYear || startMonth !== endMonth) {
        const message = 'Для месячной сводки выберите даты в пределах одного месяца.';
        setStatusMessage(message);
        setErrorExt(message);
        return;
      }

      if (startDay !== 1) {
        const message = 'Для месячной сводки дата начала должна быть первым числом месяца.';
        setStatusMessage(message);
        setErrorExt(message);
        return;
      }

      if (endDay < 28) {
        const message = 'Для месячной сводки дата окончания должна быть не раньше 28 числа выбранного месяца.';
        setStatusMessage(message);
        setErrorExt(message);
        return;
      }

      if (selectedMonth > currentMonth) {
        const message = 'Для месячной сводки нельзя выбирать будущий месяц.';
        setStatusMessage(message);
        setErrorExt(message);
        return;
      }

      if (selectedMonth === currentMonth && currentMoscowDate.getDate() < 29) {
        const message = 'Для месячной сводки текущий месяц доступен только с 29 числа по московскому времени.';
        setStatusMessage(message);
        setErrorExt(message);
        return;
      }
    }

    if (eventSource.current) {
      eventSource.current.close();
      eventSource.current = null;
    }
    if (requestAbortController.current) {
      requestAbortController.current.abort();
      requestAbortController.current = null;
    }

    const progressClientId = createProgressClientId();
    activeProgressClientIdRef.current = progressClientId;
    cancelRequestedRef.current = false;
    currentTaskIdRef.current = null;

    setIsLoading(true);
    setProgress(0);
    setStatusMessage('Подготовка к загрузке данных...');
    setProcessingStage(null);
    setStageDetails({ processed: 0, total: 0 });
    setVkAnalysisResult(null);
    setErrorExt(null); // Сбрасываем внешнюю ошибку перед новым анализом
    setStartTime(Date.now());
    setAnalysisEndTime(null);
    setCurrentTaskId(null); // Сброс ID предыдущей задачи

    const formData = new FormData();
    if (foundersFile.isDefaultFile === true) {
      formData.append('source', 'default');
    } else if (foundersFile instanceof File) {
      formData.append('foundersFile', foundersFile);
      formData.append('source', 'custom');
    } else {
      console.error('Unknown foundersFile format:', foundersFile);
      setStatusMessage('Не удалось определить источник файла учредителей.');
      setErrorExt('Не удалось определить источник файла учредителей.');
      setIsLoading(false);
      return;
    }
    formData.append('startDate', startDate);
    formData.append('endDate', endDate);
    formData.append('postThreshold', postThreshold.toString());
    formData.append('analysisMode', analysisMode);
    formData.append('progressClientId', progressClientId);

    let eventSourceUrl = `${resolvedApiBaseUrlRef.current}/api/fetch-vk-data-progress?clientId=${encodeURIComponent(progressClientId)}`;
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      const separator = eventSourceUrl.includes('?') ? '&' : '?';
      eventSourceUrl = `${eventSourceUrl}${separator}token=${encodeURIComponent(token)}`;
    }
    eventSource.current = new EventSource(eventSourceUrl);
    eventSource.current.onmessage = handleSSEProgress;
    eventSource.current.onerror = (error) => {
      console.error('SSE error:', error);
      if (eventSource.current) {
        eventSource.current.close();
        eventSource.current = null;
      }
      if (!cancelRequestedRef.current && activeProgressClientIdRef.current === progressClientId) {
        setStatusMessage('Соединение прогресса прервано, ожидаю финальный ответ сервера...');
      }
    };

    setStatusMessage('Подключение к API ВКонтакте...');
    setProgress(5);

    const abortController = new AbortController();
    requestAbortController.current = abortController;

    try {
      const response = await apiClient.post('/fetch-vk-data', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: abortController.signal,
      });
      if (activeProgressClientIdRef.current !== progressClientId) {
        return;
      }
      if (eventSource.current) {
        eventSource.current.close();
        eventSource.current = null;
      }
      setAnalysisEndTime(new Date());
      setProgress(100);
      if (response?.data?.cancelled) {
        setStatusMessage(response.data.message || 'Анализ остановлен пользователем.');
        setVkAnalysisResult(null);
        setAnalysisEndTime(null);
        setProgress(0);
      } else if (response && response.data && response.data.results) {
        setStatusMessage('Анализ данных на сервере завершен!');
        setVkAnalysisResult(response.data);
        if (response.data.taskId) {
            currentTaskIdRef.current = response.data.taskId;
            setCurrentTaskId(response.data.taskId); // Если taskId пришел в финальном ответе
        }
      } else {
        console.warn('Некорректный формат успешного ответа от сервера:', response?.data);
        throw new Error('Получен некорректный или пустой ответ от сервера.');
      }
    } catch (error) {
      console.error('Ошибка при загрузке данных из VK:', error);
      if (activeProgressClientIdRef.current !== progressClientId) {
        return;
      }
      const isCancelledRequest = cancelRequestedRef.current || error.code === 'ERR_CANCELED' || error.name === 'CanceledError';
      if (isCancelledRequest || error.response?.data?.cancelled) {
        setStatusMessage(error.response?.data?.message || 'Анализ остановлен пользователем.');
        setErrorExt(null);
        setVkAnalysisResult(null);
        setAnalysisEndTime(null);
        setProgress(0);
        return;
      }
      let detailedErrorMsg = 'Неизвестная ошибка.';
      if (error.response) {
        detailedErrorMsg = error.response.data?.error || `Ошибка сервера: ${error.response.status}`;
      } else if (error.request) {
        detailedErrorMsg = 'Ошибка сети или сервер недоступен.';
      } else {
        detailedErrorMsg = error.message || 'Произошла ошибка при подготовке запроса.';
      }
      setStatusMessage(`Ошибка: ${detailedErrorMsg}`);
      setErrorExt(detailedErrorMsg);
      setVkAnalysisResult(null);
      setAnalysisEndTime(null);
      setProgress(0);
      if (eventSource.current) {
        eventSource.current.close();
        eventSource.current = null;
      }
    } finally {
      if (activeProgressClientIdRef.current === progressClientId) {
        setIsLoading(false);
        activeProgressClientIdRef.current = null;
      }
      if (requestAbortController.current === abortController) {
        requestAbortController.current = null;
      }
    }
  }, [foundersFile, startDate, endDate, postThreshold, analysisMode, handleSSEProgress, setErrorExt]);

  const handleStopAnalysis = useCallback(() => {
    cancelRequestedRef.current = true;

    if (eventSource.current) {
      eventSource.current.close();
      eventSource.current = null;
      console.log('SSE connection closed by user.');
    }

    const taskIdToCancel = currentTaskIdRef.current || currentTaskId;
    
    resetAnalysisState(); // Сбрасываем состояния
    setStatusMessage('Анализ остановлен пользователем.');
    setErrorExt(null); // Сбрасываем и внешнюю ошибку
    
    if (taskIdToCancel) {
      console.log(`Sending cancellation request for task ID: ${taskIdToCancel}`);
      apiClient.post('/task/cancel', { taskId: taskIdToCancel })
        .then(response => {
          console.log('Server cancellation response:', response.data.message);
        })
        .catch(err => {
          console.error('Error sending cancellation request to server:', err);
        });
    }

    if (requestAbortController.current) {
      requestAbortController.current.abort();
      requestAbortController.current = null;
    }
    activeProgressClientIdRef.current = null;
    currentTaskIdRef.current = null;
    setCurrentTaskId(null); // Сброс ID после отправки запроса
  }, [currentTaskId, resetAnalysisState, setErrorExt]);

  useEffect(() => {
    let intervalId = null;
    if (isLoading && startTime) {
      intervalId = setInterval(() => {
        const secondsPassed = Math.floor((Date.now() - startTime) / 1000);
        setElapsedTime(secondsPassed);
      }, 1000);
    } else {
      if (!isLoading && startTime) {
        // setStartTime(null); // Сброс startTime теперь в resetAnalysisState или при новом запуске
      }
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLoading, startTime]);
  
  // Этот useEffect для закрытия SSE при размонтировании всего App компонента
  // должен остаться в App.js, если eventSource.current там создается/управляется
  // Если eventSource.current полностью инкапсулирован в этом хуке, то и cleanup здесь.
  useEffect(() => {
    // Возвращаем функцию для очистки при размонтировании хука
    return () => {
      if (eventSource.current) {
        console.log('Cleaning up SSE connection from useAnalysisProcess unmount');
        eventSource.current.close();
        eventSource.current = null;
      }
      if (requestAbortController.current) {
        requestAbortController.current.abort();
        requestAbortController.current = null;
      }
    };
  }, []); // Пустой массив зависимостей, чтобы выполнился один раз при монтировании/размонтировании хука

  return {
    isLoading,
    progress,
    statusMessage,
    setStatusMessage,
    processingStage,
    stageDetails,
    estimatedTimeLeft,
    elapsedTime,
    processingSpeed,
    currentItem,
    analysisEndTime, // Передаем analysisEndTime для использования в экспорте
    currentTaskId,
    vkAnalysisResult,
    fetchDataFromVK,
    handleStopAnalysis,
    // Не возвращаем eventSource.current напрямую, его жизненный цикл управляется внутри хука
  };
}; 
